/*!
 * AJBIR Atlas — walking babouches.
 * One drawing of a babouche seen from above (toe up, 80 units long), shared through <use> by every stage:
 *   « Ronde des babouches »: a pair walks round the photo and its prints draw the circle;
 *   « Ahidous »: pairs dance in a ring round the title;
 *   the walking stamp: a small pair walks in the middle of the round « Fait main » stamp;
 *   the lap in the « Added to cart » card: a small pair walks once round the photo.
 * A stage is an <svg data-aj-bb="ronde|ahidous|stamp|lap"> holding empty <g data-prints>, <g data-shadows> and
 * <g data-shoes> layers. Animations run only while on screen. When the visitor's device asks for reduced motion,
 * every stage shows a still picture (the circle complete) and the play button of its section can start it.
 * Vanilla JS, no dependency; safe to load more than once.
 */
(() => {
  'use strict';
  const AJ = (window.AJBIR = window.AJBIR || {});
  if (AJ.Babouches) return;

  const NS = 'http://www.w3.org/2000/svg';
  const TAU = Math.PI * 2;
  const DEG = 180 / Math.PI;
  const r2 = (n) => Math.round(n * 100) / 100;
  const r4 = (n) => Math.round(n * 10000) / 10000;
  const mod = (a, n) => ((a % n) + n) % n;
  const ease = (p) => (p <= 0 ? 0 : p >= 1 ? 1 : 0.5 - 0.5 * Math.cos(Math.PI * p));
  const make = (tag, attrs, parent) => {
    const node = document.createElementNS(NS, tag);
    Object.keys(attrs).forEach((k) => node.setAttribute(k, attrs[k]));
    if (parent) parent.appendChild(node);
    return node;
  };

  /* ---------- The drawing, added to the page once ---------- */
  const OUTLINE = 'M-1.5-40C7-40 13.5-33 15.5-22C17-12 16-2 15 8C14 18 13.5 26 12.5 31C11 37.5 6.5 40 0 40C-6.5 40-11 37.5-12 31C-13 24-12.5 14-13 6C-13.5-2-16-10-15.5-20C-15-31-10-40-1.5-40Z';
  const OPENING = 'M-9 9C-9 3.4-5 1 .2 1C5.4 1 9.6 3.4 9.6 9L9.3 27.5C9.1 32.6 5.2 35.2 .2 35.2C-4.8 35.2-8.7 32.6-8.8 27.5Z';
  const DEFS =
    '<defs>' +
    `<path id="AjBbOut" d="${OUTLINE}"/><path id="AjBbOpen" d="${OPENING}"/>` +
    '<clipPath id="AjBbVamp"><rect x="-20" y="-44" width="40" height="46"/></clipPath>' +
    '<clipPath id="AjBbInside"><use href="#AjBbOpen"/></clipPath>' +
    // a right babouche: sole, upper, embroidery (medallion, lozenges, triangles, zigzags), then the opening
    '<g id="AjBbShoe">' +
    '<use href="#AjBbOut" class="ajbb-sole"/><use href="#AjBbOut" class="ajbb-line"/><use href="#AjBbOut" class="ajbb-upper"/>' +
    '<g clip-path="url(#AjBbVamp)"><use href="#AjBbOut" class="ajbb-st1" transform="scale(.84 .93)"/><use href="#AjBbOut" class="ajbb-st2" transform="scale(.7 .85)"/></g>' +
    '<path class="ajbb-f1" d="M-12.3-.6-5.6-1.6-11.6-9.4Z"/><path class="ajbb-f1" d="M13.4-.6 6.6-1.6 12.9-9.4Z"/>' +
    '<path class="ajbb-arm" d="M0-12.4V-3.2M0-21.6V-26.6M-4.6-17H-7.2M4.6-17H7.4"/>' +
    '<path class="ajbb-f6" d="M-4.4-21.6-8.6-26-2.6-25.6Z"/><path class="ajbb-f3" d="M4.4-21.6 8.6-26 2.6-25.6Z"/>' +
    '<path class="ajbb-f2" d="M-4.4-12.4-8.6-8-2.6-8.4Z"/><path class="ajbb-f5" d="M4.4-12.4 8.6-8 2.6-8.4Z"/>' +
    '<path class="ajbb-dk" d="M-3.1-20.2-5.7-23.3-1.8-22.6ZM3.1-20.2 5.7-23.3 1.8-22.6ZM-3.1-13.8-5.7-10.7-1.8-11.4ZM3.1-13.8 5.7-10.7 1.8-11.4Z"/>' +
    '<path class="ajbb-f1" d="M0-2 4.2-6.6 0-11.2-4.2-6.6Z"/><path class="ajbb-f4" d="M0-4.6 1.8-6.6 0-8.6-1.8-6.6Z"/>' +
    '<path class="ajbb-f1" d="M0-26 3.8-30.3 0-34.6-3.8-30.3Z"/><path class="ajbb-f4" d="M0-28.2 1.7-30.3 0-32.4-1.7-30.3Z"/>' +
    '<path class="ajbb-dia" d="M-10.1-13.6-7.2-17-10.1-20.4-13-17ZM10.5-13.6 13.4-17 10.5-20.4 7.6-17Z"/>' +
    '<path class="ajbb-f2" d="M-10.1-15.6-8.8-17-10.1-18.4-11.4-17ZM10.5-15.6 11.8-17 10.5-18.4 9.2-17Z"/>' +
    '<circle class="ajbb-f6" cy="-17" r="4.7"/><circle class="ajbb-f2b" cy="-17" r="3.3"/><circle class="ajbb-f2" cy="-17" r="1.9"/><circle class="ajbb-f3" cy="-17" r=".8"/>' +
    '<polyline class="ajbb-zig" points="-10.7,8.97 -11.91,6.73 -9.95,4.94 -10.01,2.24 -7.32,1.5 -6.12,-.84 -3.61,-.25 -1.81,-1.99 .29,-.7 2.35,-1.98 4.14,-.22 6.64,-.82 7.84,1.52 10.53,2.21 10.51,4.92 12.5,6.71 11.3,8.97"/>' +
    '<polyline class="ajbb-zig2" points="-11.8,11 -10.4,13 -11.8,15 -10.4,17 -11.8,19 -10.4,21 -11.6,23 -10.4,25 -11.2,27"/>' +
    '<polyline class="ajbb-zig2" points="12.6,11 11.2,13 12.6,15 11.2,17 12.6,19 11.2,21 12.4,23 11.1,25 11.7,27"/>' +
    '<use href="#AjBbOpen" class="ajbb-insole"/>' +
    '<g clip-path="url(#AjBbInside)">' +
    '<path class="ajbb-shade" d="M-9.6 9.6C-9.6 3.2-5 .4.2.4 5.4.4 10.2 3.2 10.2 9.6 6.8 6.4-6.4 6.4-9.6 9.6Z"/>' +
    '<path class="ajbb-lin1" d="M-8 34.6-8.8 27.5M2.2 36.3 2.4 30.4"/><path class="ajbb-lin2" d="M-4.6 36-5.4 29.6M5.6 35.8 6.2 29.6"/><path class="ajbb-lin3" d="M-1.2 36.4-1.4 30.4M8.6 33.8 9.4 27.5"/>' +
    '<path class="ajbb-shade2" d="M-9.4 26.8C-9.4 33-5.2 35.8.2 35.8 5.6 35.8 9.8 33 9.8 26.8 6.6 31.2-6.4 31.2-9.4 26.8Z"/>' +
    '</g>' +
    '<use href="#AjBbOpen" class="ajbb-pipe"/>' +
    '</g>' +
    // the print a babouche leaves: its sole in running stitch, with the lozenge of the theme
    '<g id="AjBbPrint"><use href="#AjBbOut" class="ajbb-pr-line"/><path class="ajbb-pr-loz" d="M0-25.5 5-18.5 0-11.5-5-18.5Z"/><circle class="ajbb-pr-dot" cy="-18.5" r="1.6"/><circle class="ajbb-pr-dot" cx=".2" cy="25" r="2.6"/></g>' +
    // small print (stamp, cart): the plain sole
    '<g id="AjBbPrintS"><use href="#AjBbOut" class="ajbb-pr-solid"/></g>' +
    '<radialGradient id="AjBbShadow"><stop offset="0" stop-color="#4A322C" stop-opacity=".30"/><stop offset=".62" stop-color="#4A322C" stop-opacity=".14"/><stop offset="1" stop-color="#4A322C" stop-opacity="0"/></radialGradient>' +
    '</defs>';

  // Colours of the drawn babouches: "rouge" (embroidered red, like the photo), "terracotta" and "sable" (theme colours)
  const CSS =
    '.aj-bb-defs{position:absolute;width:0;height:0;overflow:hidden}' +
    '[data-aj-bb]{--bb-sole:#262427;--bb-line:#8FC6E6;--bb-upper:#D12039;--bb-pipe:#A01428;--bb-insole:#F0E0C6;--bb-shade:#6E1F22;' +
    '--bb-c1:#14A65F;--bb-c2:#3FC6E6;--bb-c2b:#2B6FC0;--bb-c3:#EC3F96;--bb-c4:#F6C531;--bb-c5:#F2892A;--bb-c6:#6A3FA6;--bb-thread:#FFF7EC;' +
    '--bb-print:var(--s-accent,#AC6755)}' +
    '[data-aj-bb-colour="terracotta"]{--bb-sole:#503630;--bb-line:#DED4C7;--bb-upper:#AC6755;--bb-pipe:#8B4C3F;--bb-insole:#F3E6E1;--bb-shade:#5A3D36;' +
    '--bb-c1:#566451;--bb-c2:#E3E9DF;--bb-c2b:#566451;--bb-c3:#F3E6E1;--bb-c4:#EAE2D7;--bb-c5:#DED4C7;--bb-c6:#503630;--bb-thread:#FBF9F8}' +
    '[data-aj-bb-colour="sable"]{--bb-sole:#775147;--bb-line:#EAE2D7;--bb-upper:#E4D9CB;--bb-pipe:#B9A58F;--bb-insole:#FBF9F8;--bb-shade:#8E7A68;' +
    '--bb-c1:#566451;--bb-c2:#AC6755;--bb-c2b:#775147;--bb-c3:#9A5A4A;--bb-c4:#AC6755;--bb-c5:#566451;--bb-c6:#503630;--bb-thread:#FBF9F8}' +
    '.ajbb-sole{fill:none;stroke:var(--bb-sole);stroke-width:6;stroke-linejoin:round}' +
    '.ajbb-line{fill:none;stroke:var(--bb-line);stroke-width:2.6;stroke-linejoin:round}' +
    '.ajbb-upper{fill:var(--bb-upper)}' +
    '.ajbb-st1{fill:none;stroke:var(--bb-c4);stroke-width:1.5;stroke-dasharray:2.2 1.5}' +
    '.ajbb-st2{fill:none;stroke:var(--bb-thread);stroke-width:1.2;stroke-dasharray:1.6 1.4}' +
    '.ajbb-f1{fill:var(--bb-c1)}.ajbb-f2{fill:var(--bb-c2)}.ajbb-f2b{fill:var(--bb-c2b)}.ajbb-f3{fill:var(--bb-c3)}' +
    '.ajbb-f4{fill:var(--bb-c4)}.ajbb-f5{fill:var(--bb-c5)}.ajbb-f6{fill:var(--bb-c6)}.ajbb-dk{fill:var(--bb-sole)}' +
    '.ajbb-dia{fill:var(--bb-thread);stroke:var(--bb-c6);stroke-width:.9}' +
    '.ajbb-arm{fill:none;stroke:var(--bb-c4);stroke-width:1.2;stroke-linecap:round}' +
    '.ajbb-zig{fill:none;stroke:var(--bb-thread);stroke-width:1.1;stroke-linejoin:round}' +
    '.ajbb-zig2{fill:none;stroke:var(--bb-c4);stroke-width:1.1;stroke-linejoin:round}' +
    '.ajbb-insole{fill:var(--bb-insole)}.ajbb-shade{fill:var(--bb-shade);opacity:.5}.ajbb-shade2{fill:var(--bb-shade);opacity:.22}' +
    '.ajbb-lin1{fill:none;stroke:var(--bb-c4);stroke-width:1.5}.ajbb-lin2{fill:none;stroke:var(--bb-c1);stroke-width:1.5}.ajbb-lin3{fill:none;stroke:var(--bb-c2);stroke-width:1.5}' +
    '.ajbb-pipe{fill:none;stroke:var(--bb-pipe);stroke-width:2}' +
    '.ajbb-pr-line{fill:var(--bb-print);fill-opacity:.07;stroke:var(--bb-print);stroke-width:2.4;stroke-dasharray:4.2 3;stroke-linecap:round}' +
    '.ajbb-pr-loz{fill:none;stroke:var(--bb-print);stroke-width:2;stroke-linejoin:round}' +
    '.ajbb-pr-dot{fill:var(--bb-print)}.ajbb-pr-solid{fill:var(--bb-print);opacity:.78}';

  function ensureDefs() {
    if (document.getElementById('AjBbDefs')) return;
    const style = document.createElement('style');
    style.id = 'AjBbStyle';
    style.textContent = CSS;
    document.head.appendChild(style);
    const svg = make('svg', { id: 'AjBbDefs', class: 'aj-bb-defs', 'aria-hidden': 'true', focusable: 'false', width: '0', height: '0' });
    svg.innerHTML = DEFS;
    document.body.appendChild(svg);
  }

  /* ---------- One babouche on a stage: its soft shadow on the ground, and the shoe (h: how high it is lifted, 0 to 1) ---------- */
  class Shoe {
    constructor(svg, foot, scale) {
      this.m = foot === 'L' ? -1 : 1; // the left babouche is the right one mirrored
      this.s = scale;
      this.shadow = make('ellipse', { rx: 13.5, ry: 38, fill: 'url(#AjBbShadow)' }, svg.querySelector('[data-shadows]'));
      this.node = make('use', { href: '#AjBbShoe' }, svg.querySelector('[data-shoes]'));
    }
    place(x, y, rot, h) {
      const s = this.s;
      const k = s * (1 + 0.1 * h);
      const up = h * s * 7;
      this.node.setAttribute('transform', `translate(${r2(x - up * 0.35)} ${r2(y - up)}) rotate(${r2(rot)}) scale(${r4(k * this.m * 0.9)} ${r4(k)})`);
      this.shadow.setAttribute('transform', `translate(${r2(x + s * 3)} ${r2(y + s * 4.5)}) rotate(${r2(rot)}) scale(${r4(s * (0.9 + 0.12 * h))} ${r4(s * (1 + 0.12 * h))})`);
      this.shadow.setAttribute('opacity', r2(1 - 0.5 * h));
    }
  }

  /* ---------- A pair walking round a circle ----------
     Step s lands at time s × Ts; the right foot takes the even steps. Walking clockwise, the right foot is on the
     inside. Each step leaves a print that fades once the pair has gone by (down to "floor"), so the steps keep
     drawing the circle. With "laps", the pair stops after that many turns and every print stays. */
  class Ronde {
    constructor(svg, o) {
      this.o = o;
      this.lap = o.N * o.Ts;
      this.end = o.laps ? o.laps * this.lap : Infinity;
      const layer = svg.querySelector('[data-prints]');
      this.prints = [];
      this.alpha = [];
      for (let i = 0; i < o.N; i++) {
        const foot = i % 2 ? 'L' : 'R';
        const p = this.pose(i, foot, 0);
        const m = foot === 'L' ? -1 : 1;
        this.prints.push(make('use', {
          href: o.print,
          opacity: '0',
          transform: `translate(${r2(p.x)} ${r2(p.y)}) rotate(${r2(p.rot)}) scale(${r4(o.scale * m * 0.9)} ${r4(o.scale)})`,
        }, layer));
        this.alpha.push(0);
      }
      this.R = new Shoe(svg, 'R', o.scale);
      this.L = new Shoe(svg, 'L', o.scale);
    }
    pose(s, foot, h) {
      const o = this.o;
      const th = o.start + o.dir * s * TAU / o.N;
      const side = (foot === 'R' ? -1 : 1) * o.dir;
      const r = o.R + side * o.w * (1 - 0.35 * h); // the swinging foot passes close to the other one
      return { x: o.cx + r * Math.cos(th), y: o.cy + r * Math.sin(th), rot: th * DEG + (o.dir > 0 ? 180 : 0) };
    }
    render(time) {
      const o = this.o;
      const t = Math.min(time, this.end);
      const k = Math.floor(t / o.Ts + 1e-9);
      const p = t / o.Ts - k;
      const moving = (k + 1) % 2 ? 'L' : 'R';
      const h = Math.sin(Math.PI * p);
      const mv = this.pose(k - 1 + 2 * ease(p), moving, h);
      const st = this.pose(k, moving === 'R' ? 'L' : 'R', 0);
      (moving === 'R' ? this.R : this.L).place(mv.x, mv.y, mv.rot, h);
      (moving === 'R' ? this.L : this.R).place(st.x, st.y, st.rot, 0);
      const fade = o.fade ? o.fade * this.lap : 0;
      for (let i = 0; i < o.N; i++) {
        const s = k - mod(k - i, o.N);
        let a = 0;
        if (s >= -1) a = fade ? o.floor + (1 - o.floor) * Math.max(0, 1 - (t - Math.max(0, s * o.Ts)) / fade) : 1;
        a = r2(a);
        if (a !== this.alpha[i]) {
          this.alpha[i] = a;
          this.prints[i].setAttribute('opacity', a);
        }
      }
    }
  }

  /* ---------- Pairs in a ring, toes to the centre, turning together (the ahidous) ----------
     On each bar of eight beats the left foot steps out and the right one closes up, three times; then both feet
     stamp, and the drum in the middle answers. */
  class Ahidous {
    constructor(svg, o) {
      this.o = o;
      this.dw = o.w / o.R;
      this.pairs = [];
      for (let j = 0; j < o.pairs; j++) {
        this.pairs.push({ a: o.start + j * TAU / o.pairs, L: new Shoe(svg, 'L', o.scale), R: new Shoe(svg, 'R', o.scale) });
      }
      this.pulse = svg.querySelector('[data-pulse]');
      this.drum = svg.querySelector('[data-drum]');
    }
    render(t) {
      const o = this.o;
      const beat = t / o.Tb;
      const bar = Math.floor(beat / 8);
      const b = beat - bar * 8;
      let lOff = 0;
      let rOff = 0;
      let hL = 0;
      let hR = 0;
      for (let m = 0; m < 3; m++) {
        lOff += ease(b - 2 * m);
        rOff += ease(b - 2 * m - 1);
        if (b >= 2 * m && b < 2 * m + 1) hL = Math.sin(Math.PI * (b - 2 * m));
        if (b >= 2 * m + 1 && b < 2 * m + 2) hR = Math.sin(Math.PI * (b - 2 * m - 1));
      }
      const hop = b >= 6 && b < 7 ? Math.sin(Math.PI * (b - 6)) : 0;
      const base = bar * 3 * o.step;
      this.pairs.forEach((pr) => {
        const thL = pr.a + o.dir * (base + lOff * o.step + this.dw);
        const thR = pr.a + o.dir * (base + rOff * o.step - this.dw);
        const headL = (thL - o.dir * this.dw) * DEG - 90;
        const headR = (thR + o.dir * this.dw) * DEG - 90;
        pr.L.place(o.cx + o.R * Math.cos(thL), o.cy + o.R * Math.sin(thL), headL - o.dir * (o.toe + 8 * hL), Math.max(hL, hop) * 0.9);
        pr.R.place(o.cx + o.R * Math.cos(thR), o.cy + o.R * Math.sin(thR), headR + o.dir * o.toe, Math.max(hR, hop) * 0.9);
      });
      const q = b >= 7 ? b - 7 : -1;
      if (this.pulse) {
        this.pulse.setAttribute('r', r2(o.drumR + Math.max(0, q) * 34));
        this.pulse.setAttribute('opacity', q >= 0 ? r2(0.6 * (1 - q)) : 0);
      }
      if (this.drum) {
        const d = q >= 0 ? 1 + 0.018 * Math.sin(Math.PI * Math.min(1, q * 2.2)) : 1;
        this.drum.setAttribute('transform', `translate(${o.cx} ${o.cy}) scale(${r4(d)}) translate(${-o.cx} ${-o.cy})`);
      }
    }
  }

  /* ---------- Stages ---------- */
  // Geometry of each kind of stage, in the units of its viewBox. t0 / still: where the animation starts, and the
  // still picture shown without animation, in laps (the circle is already half drawn when the walk begins).
  const KINDS = {
    ronde: { cx: 300, cy: 300, R: 247, w: 15.5, N: 36, Ts: 0.5, scale: 0.76, fade: 0.82, floor: 0.26, print: '#AjBbPrint', t0: 0.55, still: 1.16 },
    stamp: { cx: 66, cy: 66, R: 29.5, w: 3.1, N: 22, Ts: 0.34, scale: 0.14, fade: 0.8, floor: 0.3, print: '#AjBbPrintS', t0: 0.6, still: 1.2 },
    lap: { cx: 50, cy: 50, R: 41, w: 3, N: 16, Ts: 0.1, scale: 0.15, fade: 0, floor: 1, laps: 1, print: '#AjBbPrintS', t0: 0, still: 1 },
    ahidous: { cx: 260, cy: 260, R: 194, w: 12, scale: 0.63, Tb: 0.42, step: 4.5 / DEG, toe: 5, drumR: 136 },
  };
  const SPEED = { slow: 1.25, normal: 1, fast: 0.8 };
  const stages = [];
  const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const motionOK = () => !(reduceMotion && reduceMotion.matches);
  let raf = 0;
  let last = 0;

  function frame(now) {
    raf = 0;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;
    let busy = false;
    stages.forEach((st) => {
      if (!st.visible || !st.playing || st.done) return;
      st.t += dt;
      if (st.t >= st.anim.end) {
        st.t = st.anim.end;
        st.done = true;
      }
      st.anim.render(st.t);
      busy = true;
    });
    if (busy) raf = requestAnimationFrame(frame);
    else last = 0;
  }
  const kick = () => {
    if (!raf) {
      last = 0;
      raf = requestAnimationFrame(frame);
    }
  };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target.ajBb) e.target.ajBb.visible = e.isIntersecting;
      });
      kick();
    }, { rootMargin: '60px 0px' })
    : null;

  function create(el) {
    const kind = el.getAttribute('data-aj-bb');
    const speed = SPEED[el.getAttribute('data-aj-bb-speed')] || 1;
    const dir = el.getAttribute('data-aj-bb-dir') === 'ccw' ? -1 : 1;
    el.querySelectorAll('[data-prints], [data-shadows], [data-shoes]').forEach((g) => { g.textContent = ''; });
    let anim;
    let t0 = 0;
    let still = 0;
    if (kind === 'ahidous') {
      const pairs = Math.max(4, Math.min(12, parseInt(el.getAttribute('data-aj-bb-pairs'), 10) || 8));
      anim = new Ahidous(el, Object.assign({}, KINDS.ahidous, { pairs, dir, Tb: KINDS.ahidous.Tb * speed, start: -Math.PI / 2 }));
      anim.end = Infinity;
    } else {
      const o = Object.assign({}, KINDS[kind] || KINDS.ronde, { dir, start: -Math.PI / 2 });
      o.Ts *= speed;
      anim = new Ronde(el, o);
      t0 = o.t0 * anim.lap;
      still = o.still * anim.lap;
    }
    const st = { el, kind, anim, t0, still, visible: !io, done: false, playing: motionOK() };
    st.t = st.playing ? t0 : still;
    anim.render(st.t);
    el.ajBb = st;
    stages.push(st);
    if (io) io.observe(el);
    return st;
  }

  function drop(st) {
    if (io) io.unobserve(st.el);
    const i = stages.indexOf(st);
    if (i > -1) stages.splice(i, 1);
    delete st.el.ajBb;
  }

  /* The pause / play button of a section: it drives every stage of its section. */
  function initToggle(btn) {
    if (btn.ajBb) return;
    btn.ajBb = true;
    const scope = btn.closest('[data-aj-bb-scope]') || btn.parentElement;
    const mine = () => stages.filter((st) => scope.contains(st.el));
    const sync = () => {
      const on = mine().some((st) => st.playing);
      btn.classList.toggle('is-paused', !on);
      btn.setAttribute('aria-label', on ? btn.getAttribute('data-label-pause') : btn.getAttribute('data-label-play'));
    };
    btn.addEventListener('click', () => {
      const on = !mine().some((st) => st.playing);
      mine().forEach((st) => { st.playing = on; });
      sync();
      kick();
    });
    btn.hidden = false;
    sync();
  }

  function mount(root) {
    const scope = root && root.querySelectorAll ? root : document;
    if (!scope.querySelector('[data-aj-bb]:not([data-aj-bb="lap"])') && !(scope.matches && scope.matches('[data-aj-bb]'))) return;
    ensureDefs();
    const els = Array.from(scope.querySelectorAll('[data-aj-bb]'));
    if (scope.matches && scope.matches('[data-aj-bb]')) els.unshift(scope);
    els.forEach((el) => {
      if (el.getAttribute('data-aj-bb') === 'lap') return; // started by the « Added to cart » card
      if (el.ajBb) drop(el.ajBb);
      create(el);
    });
    scope.querySelectorAll('[data-aj-bb-toggle]').forEach(initToggle);
    kick();
  }

  function unmount(root) {
    stages.slice().forEach((st) => {
      if (root === st.el || (root && root.contains && root.contains(st.el))) drop(st);
    });
  }

  /* The « Added to cart » card: the little pair walks once round the photo and closes the circle. */
  function lap(svg) {
    if (!svg) return null;
    ensureDefs();
    if (svg.ajBb) drop(svg.ajBb);
    const st = create(svg);
    if (st.playing) st.t = 0;
    st.anim.render(st.t);
    kick();
    return st;
  }

  AJ.Babouches = { mount, unmount, lap };

  document.addEventListener('shopify:section:load', (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document));
  else mount(document);
})();
